import { useEffect, useRef, useState } from 'react';

import { CheckIcon } from '@/components/icons';
import { Modal } from '@/components/ui/Modal';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { TimesheetDayEditor } from './TimesheetDayEditor';
import './TimesheetMonthGrid.css';
import { Button } from '@/components/ui/Button';
import type { ReferenceRef } from '@/features/portal/types';

import {
  buildCalendar,
  dayTone,
  formatDays,
  WEEKDAY_LABELS,
  type CalendarCell,
} from '../timesheet-labels';
import type { Timesheet, TimesheetDayInput } from '../types';

/** Journées d'une date, telles que la grille les manipule. */
type DraftDay = TimesheetDayInput;

const WORKED = 'TRAVAILLE';

/**
 * Grille mensuelle d'un CRA.
 *
 * ── Pourquoi un calendrier et non un tableau de lignes ───────────────────
 * On déclare un mois en le parcourant : « le 3 j'étais en clientèle, le 4 en
 * congé ». Un tableau trié par date perd la forme de la semaine — on ne voit
 * plus qu'un jour tombe un vendredi, ni qu'un pont a été oublié.
 *
 * Une date peut porter **deux lignes** : une demi-journée travaillée et une
 * demi-journée de congé. La grille les empile dans la même case, et le total
 * de la case dit immédiatement si la journée est complète.
 *
 * « Tout cocher » marque les dates de mission du mois comme travaillées ;
 * l’éditeur permet ensuite de préciser les exceptions.
 */
export const TimesheetMonthGrid = ({
  timesheet,
  readOnly = false,
  dayTypes,
  workModels,
  holidays,
  pending,
  error,
  defaultWorkModelId = '',
  onChange,
  onInteractionBusyChange,
}: {
  timesheet: Timesheet;
  readOnly?: boolean;
  dayTypes: ReferenceRef[];
  workModels: ReferenceRef[];
  /** Dates fériées du mois, `AAAA-MM-JJ`. */
  holidays: string[];
  pending: boolean;
  error?: string | undefined;
  defaultWorkModelId?: string;
  /** Le mois complet après modification — le serveur remplace en bloc. */
  onChange: (days: DraftDay[]) => Promise<void>;
  /** Empêche la soumission pendant le délai qui distingue clic et double clic. */
  onInteractionBusyChange?: (busy: boolean) => void;
}) => {
  const canEdit = timesheet.permissions.canEdit && !readOnly;
  const [openDate, setOpenDate] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [queued, setQueued] = useState(false);
  const [saving, setSaving] = useState(false);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const changes = useRef(new Map<string, 'toggle' | 'half'>());
  const writeLock = useRef(false);
  const busy = pending || saving;

  useEffect(() => {
    onInteractionBusyChange?.(queued || saving);
  }, [queued, saving, onInteractionBusyChange]);
  useEffect(() => () => onInteractionBusyChange?.(false), [onInteractionBusyChange]);

  useEffect(
    () => () => {
      if (clickTimer.current) clearTimeout(clickTimer.current);
    },
    [],
  );

  const weeks = buildCalendar(
    timesheet.periodMonth,
    timesheet.mission.startDate,
    timesheet.mission.endDate,
  );

  const holidaySet = new Set(holidays);
  const workedType = dayTypes.find((type) => type.code === WORKED);

  /* Le mois courant, à plat : c'est cette liste qu'on renvoie modifiée. */
  const current: DraftDay[] = timesheet.days.map((day) => ({
    date: day.date,
    value: day.value,
    dayTypeId: day.dayType.id,
    workModelId: day.workModel?.id ?? null,
    comment: day.comment,
  }));

  const byDate = new Map<string, typeof timesheet.days>();
  for (const day of timesheet.days) {
    byDate.set(day.date, [...(byDate.get(day.date) ?? []), day]);
  }

  const totalOf = (date: string): number =>
    (byDate.get(date) ?? []).reduce((sum, day) => sum + Number(day.value), 0);

  /** Remplace les lignes d'une date, en laissant le reste du mois intact. */
  const setDate = async (date: string, lines: DraftDay[]): Promise<void> => {
    await onChange([...current.filter((day) => day.date !== date), ...lines]);
    setOpenDate(null);
  };

  // Un seul remplacement du mois pour les clics rapprochés, y compris sur
  // plusieurs dates. Le double clic remplace le simple clic avant la sauvegarde.
  const scheduleDay = (date: string, action: 'toggle' | 'half'): void => {
    if (busy || writeLock.current || !workedType || !canEdit) return;
    changes.current.set(date, action);
    setQueued(true);
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      let next = [...current];
      for (const [changedDate, kind] of changes.current) {
        const existing = current.filter((day) => day.date === changedDate);
        const worked = existing.find((day) => day.dayTypeId === workedType.id);
        next = next.filter((day) => day.date !== changedDate);
        if (kind === 'half' || !worked) {
          next.push({
            date: changedDate,
            value: kind === 'half' ? '0.5' : '1',
            dayTypeId: workedType.id,
            workModelId: worked?.workModelId ?? (defaultWorkModelId || null),
            comment: worked?.comment ?? null,
          });
        }
      }
      changes.current.clear();
      clickTimer.current = null;
      writeLock.current = true;
      setSaving(true);
      setQueued(false);
      void onChange(next)
        .catch(() => {
          // La fiche affiche l’erreur ; aucune donnée locale n’est conservée à tort.
        })
        .finally(() => {
          writeLock.current = false;
          setSaving(false);
        });
    }, 300);
  };

  const checkAll = (): void => {
    if (!workedType) return;
    const days = weeks.flat().filter((cell) => cell && !cell.isOutsideMission);
    void onChange(
      days.map((cell) => ({
        date: cell!.date,
        value: '1',
        dayTypeId: workedType.id,
        workModelId: defaultWorkModelId || null,
        comment: current.find((day) => day.date === cell!.date)?.comment ?? null,
      })),
    ).catch(() => {
      // L’erreur est affichée par la fiche.
    });
  };

  const fillDays = (type: ReferenceRef): void => {
    const declared = new Set(current.map((day) => day.date));
    const added: DraftDay[] = [];

    for (const week of weeks) {
      for (const cell of week) {
        if (!cell || cell.isWeekend || cell.isOutsideMission) continue;
        if (holidaySet.has(cell.date) || declared.has(cell.date)) continue;

        added.push({
          date: cell.date,
          value: '1',
          dayTypeId: type.id,
          workModelId: type.code === WORKED ? defaultWorkModelId || null : null,
        });
      }
    }

    void onChange([...current, ...added]).catch(() => {
      /* L’erreur est affichée par la fiche. */
    });
  };

  return (
    <div className="cra-calendar">
      {canEdit ? (
        <div className="cra-calendar__toolbar">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={busy || queued || !workedType}
            onClick={checkAll}
          >
            <CheckIcon aria-hidden /> Tout cocher
          </Button>
          {dayTypes.find((type) => type.code === 'ABSENCE') && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy || queued}
              onClick={() => {
                const absence = dayTypes.find((type) => type.code === 'ABSENCE');
                if (absence) fillDays(absence);
              }}
            >
              Marquer absent
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy || queued || current.length === 0}
            onClick={() => {
              setClearing(true);
            }}
          >
            Tout effacer
          </Button>
          <span className="cra-calendar__hint">
            Tout cocher inclut les week-ends et remplace les natures déjà saisies, dans les dates de
            mission.
          </span>
        </div>
      ) : null}

      <div className="cra-calendar__instructions">
        <span>
          <strong>1 clic</strong> Travaillé / non travaillé
        </span>
        <span>
          <strong>Double clic</strong> Demi-journée
        </span>
        <Button size="sm" variant="ghost" onClick={() => setGuideOpen(true)}>
          Guide d’utilisation
        </Button>
      </div>

      <div
        className="cra-calendar__scroll"
        tabIndex={0}
        role="region"
        aria-label="Calendrier mensuel"
      >
        <table className="cra-calendar__table">
          <caption className="sr-only">Journées déclarées du mois</caption>
          <thead>
            <tr>
              {WEEKDAY_LABELS.map((label) => (
                <th key={label} scope="col" className="cra-calendar__weekday">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, index) => (
              <tr key={index}>
                {week.map((cell, cellIndex) => (
                  <td key={cell?.date ?? `vide-${String(cellIndex)}`} className="align-top">
                    {cell ? (
                      <DayCell
                        cell={cell}
                        lines={byDate.get(cell.date) ?? []}
                        total={totalOf(cell.date)}
                        isHoliday={holidaySet.has(cell.date)}
                        editable={canEdit}
                        disabled={busy || !workedType}
                        editDisabled={busy || queued}
                        onToggle={() => scheduleDay(cell.date, 'toggle')}
                        onHalf={() => scheduleDay(cell.date, 'half')}
                        onOpen={() => {
                          setOpenDate(cell.date);
                        }}
                      />
                    ) : null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {guideOpen && (
        <Modal title="Guide d’utilisation" onClose={() => setGuideOpen(false)}>
          <div className="cra-calendar__guide">
            <p>
              Par défaut, les journées sans activité sont non travaillées. Cliquez sur l’ensemble de
              la case pour changer son état.
            </p>
            <dl>
              <div>
                <dt>Travaillé / non travaillé</dt>
                <dd>Un clic sur la case.</dd>
              </div>
              <div>
                <dt>Demi-journée</dt>
                <dd>
                  Un double clic, ou « Éditer » puis « Demi-journée » sur mobile et au clavier.
                </dd>
              </div>
              <div>
                <dt>Congés, RTT et jours fériés</dt>
                <dd>
                  Le bouton « Éditer » apparaît au survol ou au focus de la case et reste visible
                  sur écran tactile. Choisissez la nature et la durée, puis enregistrez.
                </dd>
              </div>
              <div>
                <dt>Tout cocher</dt>
                <dd>
                  Toutes les dates du mois comprises dans la mission deviennent travaillées,
                  week-ends compris. Les congés et autres natures déjà saisis sont remplacés.
                </dd>
              </div>
            </dl>
            <p>
              Les modifications sont enregistrées automatiquement. Les journées hors mission et les
              CRA en lecture seule ne sont pas modifiables.
            </p>
          </div>
        </Modal>
      )}
      <div className="cra-calendar__legend">
        <span>
          <i data-tone="worked" />
          Travail
        </span>
        <span>
          <i data-tone="absence" />
          Absence / congé
        </span>
        <span>
          <i data-tone="rest" />
          Week-end / férié
        </span>
        <span>½ Journée partielle</span>
      </div>

      {openDate && canEdit ? (
        <TimesheetDayEditor
          key={openDate}
          date={openDate}
          pending={pending}
          error={error}
          lines={current.filter((day) => day.date === openDate)}
          dayTypes={dayTypes}
          workModels={workModels}
          defaultWorkModelId={defaultWorkModelId}
          onCancel={() => {
            setOpenDate(null);
          }}
          onApply={(lines) => {
            return setDate(openDate, lines);
          }}
        />
      ) : null}
      {clearing && canEdit && (
        <ConfirmationModal
          title="Effacer les journées du mois ?"
          description="Toutes les journées déclarées de ce CRA seront retirées."
          confirmLabel="Effacer les journées"
          pending={pending}
          error={error}
          onCancel={() => setClearing(false)}
          onConfirm={() => {
            void onChange([])
              .then(() => setClearing(false))
              .catch(() => {
                /* Le modal affiche l’erreur. */
              });
          }}
        />
      )}
    </div>
  );
};

/** Une case du calendrier. */
const DayCell = ({
  cell,
  lines,
  total,
  isHoliday,
  editable,
  disabled,
  editDisabled,
  onToggle,
  onHalf,
  onOpen,
}: {
  cell: CalendarCell;
  lines: Timesheet['days'];
  total: number;
  isHoliday: boolean;
  editable: boolean;
  disabled: boolean;
  editDisabled: boolean;
  onToggle: () => void;
  onHalf: () => void;
  onOpen: () => void;
}) => {
  /* Hors mission : la case reste visible pour garder la forme du mois, mais
     rien ne peut y être déclaré — le serveur le refuserait. */
  const inert = cell.isOutsideMission;

  const base = 'cra-calendar__day';
  const background = inert
    ? 'cra-calendar__day--outside'
    : cell.isWeekend || isHoliday
      ? 'cra-calendar__day--rest'
      : '';
  const content = (
    <>
      <span className="cra-calendar__day-heading">
        <span className="cra-calendar__number">{cell.dayOfMonth}</span>
        {/* Une journée incomplète se voit sans lire les libellés. */}
        {total > 0 && total < 1 ? (
          <span className="text-xs font-medium text-amber-600" title="Journée incomplète">
            ½
          </span>
        ) : null}
      </span>

      {isHoliday ? <span className="text-ink-400 text-[11px] leading-tight">Férié</span> : null}

      {!lines.length && !inert && !isHoliday && (
        <span className="cra-calendar__empty">Non travaillé</span>
      )}
      {lines.map((line) => (
        <span
          key={line.id}
          className={`cra-calendar__entry ${dayTone(line.dayType.code)}`}
          title={`${line.dayType.label} — ${formatDays(line.value)} jour`}
        >
          {formatDays(line.value)} {line.dayType.label}
        </span>
      ))}
    </>
  );

  if (!editable || inert) {
    return <div className={`${base} ${background}`}>{content}</div>;
  }

  return (
    <div className="cra-calendar__cell">
      <button
        type="button"
        onClick={(event) => {
          if (event.detail < 2) onToggle();
        }}
        onDoubleClick={onHalf}
        disabled={disabled}
        aria-label={`${new Date(`${cell.date}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} : ${lines.length ? lines.map((line) => `${formatDays(line.value)} jour ${line.dayType.label}`).join(', ') : 'Non travaillé'}`}
        className={`${base} ${background}`}
      >
        {content}
      </button>
      <button
        type="button"
        className="cra-calendar__edit"
        disabled={editDisabled}
        onClick={onOpen}
        aria-label={`Modifier le ${new Date(`${cell.date}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`}
      >
        Éditer
      </button>
    </div>
  );
};
